<template>
  <div class="column content-box">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top"
      icon="add-circle"
      @click="openAddPopup()"
    >
      {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
    </woot-button>

    <!-- List Agents -->
    <div class="row">
      <div class="small-8 columns with-right-space">
        <search-form          
          :on-search-submit="onSearchSubmit"          
        />
      </div>
    </div>
    <div class="row">
      <div class="small-8 columns with-right-space">        
        <woot-loading-state
          v-if="uiFlags.isFetching"
          :message="$t('AGENT_MGMT.LOADING')"
        />
        <div v-else>
          <p v-if="!agentList.length">
            {{ $t('AGENT_MGMT.LIST.404') }}
          </p>
          <table v-else class="woot-table">
            <tbody>
              <tr v-for="(agent, index) in agentList" :key="agent.email">
                <!-- Gravtar Image -->
                <td>
                  <thumbnail
                    :src="agent.thumbnail"
                    class="columns"
                    :username="agent.name"
                    size="40px"
                    :status="agent.availability_status"
                  />
                </td>
                <!-- Agent Name + Email -->
                <td>
                  <span class="agent-name">
                    {{ agent.name }}
                  </span>
                  <span>{{ agent.email }}</span>
                </td>
                <!-- Agent Role + Verification Status -->
                <td v-if="!isAccountTypeParent">
                  <span class="agent-name">
                    {{
                      $t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`)
                    }}
                  </span>
                  <span v-if="agent.confirmed">
                    {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                  </span>
                  <span v-if="!agent.confirmed">
                    {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                  </span>
                </td>
                <td v-else>
                  <span v-if="agent.account_users">
                    {{ $t(`AGENT_MGMT.LIST.COUNT_ACCOUNT${agent.account_users.length > 1 ? 'S' : ''}`, { count: ( agent.account_users).length }) }}
                  </span>
                  <span v-else>---</span>
                </td>
                <!-- Actions -->
                <td class="column-actions">
                  <div class="button-wrapper">
                    <woot-button
                      v-if="showEditAction(agent)"
                      v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                      variant="smooth"
                      size="tiny"
                      color-scheme="secondary"
                      icon="edit"
                      class-names="grey-btn"
                      @click="openEditPopup(agent)"
                    />
                    <woot-button
                      v-if="showCustomAttributeAction(agent)"
                      v-tooltip.top="$t('AGENT_MGMT.ATTRIBUTE.BUTTON_TEXT')"
                      variant="smooth"
                      size="tiny"
                      color-scheme="secondary"
                      icon="code"
                      class-names="grey-btn"
                      @click="openCustomAttributePopup(agent)"
                    />
                    <woot-button
                      v-if="showDeleteAction(agent)"
                      v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                      variant="smooth"
                      color-scheme="alert"
                      size="tiny"
                      icon="dismiss-circle"
                      class-names="grey-btn"
                      :is-loading="loading[agent.id]"
                      @click="openDeletePopup(agent, index)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <table-footer
            :current-page="Number(meta.current_page)"
            :total-count="(meta.count ? meta.count : agentList.length)"
            :page-size="(meta.count ? 15 : agentList.length)"
            @page-change="onPageChange"
          />
        </div>
      </div>
      <div class="small-4 columns">
        <span
          v-dompurify-html="
            useInstallationName(
              $t('AGENT_MGMT.SIDEBAR_TXT'),
              globalConfig.installationName
            )
          "
        />
      </div>
    </div>
    <!-- Add Agent -->
    <woot-modal v-if="!isAccountTypeParent" :show.sync="showAddPopup" :on-close="hideAddPopup">      
      <add-agent :on-close="hideAddPopup"/>
    </woot-modal>
    <woot-modal v-else :show.sync="showAddPopup" :on-close="hideAddPopup">      
      <add-agent-multi-account :on-close="hideAddPopup"/>
    </woot-modal>
    <!-- Edit Agent -->
    <woot-modal  v-if="!isAccountTypeParent" :show.sync="showEditPopup" :on-close="hideEditPopup">
      <edit-agent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :rules="currentAgent.rules"
        :enable_template_limit="currentAgent.enable_template_limit"
        :daily_template_limit="currentAgent.daily_template_limit"
        :monthly_template_limit="currentAgent.monthly_template_limit"
        :availability="currentAgent.availability_status"
        :teams="currentAgent.teams"
        :inboxes="currentAgent.inboxes"
        :on-close="hideEditPopup"
      />
    </woot-modal>
    <woot-modal v-else  :show.sync="showEditPopup" :on-close="hideEditPopup">
      <edit-agent-multi-account v-if="showEditPopup" :agent="currentAgent" :on-close="hideEditPopup"/>
    </woot-modal>
    <!-- Edit Agent -->
    <woot-modal
      :show.sync="showCustomAttributePopup"
      :on-close="hideCustomAttributePopup"
      :close-on-backdrop-click="false"
      modal-type="right-aligned"
    >
      <custom-attribute-agent
        :on-close="hideCustomAttributePopup"
        :agent="this.currentAgent"
      />
    </woot-modal>
    <!-- Delete Agent -->
    <woot-delete-modal
      :show.sync="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="deleteMessage"
      :message-value="deleteValue"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import Thumbnail from '../../../../components/widgets/Thumbnail';
import AddAgent from './AddAgent';
import EditAgent from './EditAgent';
import CustomAttributeAgent from './CustomAttributeAgent';
import SearchForm from './SearchForm.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import AddAgentMultiAccount from './AddAgentMultiAccount';
import EditAgentMultiAccount from './EditAgentMultiAccount';


const DEFAULT_PAGE = 1;

export default {
  components: {
    AddAgent,
    EditAgent,
    Thumbnail,
    CustomAttributeAgent,
    TableFooter,
    SearchForm,
    AddAgentMultiAccount,
    EditAgentMultiAccount,
  },
  mixins: [globalConfigMixin],
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showDeletePopup: false,
      showEditPopup: false,
      showCustomAttributePopup: false,
      agentAPI: {
        message: '',
      },
      currentAgent: {},
      agentRoles: ['administrator', 'supervisor', 'team_supervisor'],      
      search: {
        q: '',
        account_id: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      uiFlags: 'agents/getUIFlags',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
      meta: 'agents/getMeta',
      isAccountTypeParent: 'isAccountTypeParent',
    }),
    deleteConfirmText() {
      return `${this.$t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${
        this.currentAgent.name
      }`;
    },
    deleteRejectText() {
      return `${this.$t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${
        this.currentAgent.name
      }`;
    },
    deleteMessage() {      
      let message = this.$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE');
      if(this.isAccountTypeParent && (this.currentAgent.account_users ?? []).length > 1){
        message = `${this.$t("AGENT_MGMT.DELETE.CONFIRM.MESSAGE_MULTIACCOUNT", { name: this.currentAgent.name, count: this.currentAgent.account_users.length })}\n${message}`;
      }
      return message;      
    },
    deleteValue() {
      return ` ${this.currentAgent.name}?`;
    },    
    pageParameter() {
      const selectedPageNumber = Number(this.$route.query?.page);

      return !Number.isNaN(selectedPageNumber) &&
        selectedPageNumber >= DEFAULT_PAGE
        ? selectedPageNumber
        : DEFAULT_PAGE;
    }    
  },
  mounted() {
    if(this.getDataSearchFromQueryParams()){
      this.search = this.getDataSearchFromQueryParams();
      this.fetch(this.pageParameter, false);
    }else{
      this.fetch(this.pageParameter);
    }
  },
  methods: {
    showEditAction(agent) {
      return this.currentUserId !== agent.id;
    },
    showCustomAttributeAction(agent) {
      return this.currentUserId !== agent.id && !this.isAccountTypeParent;
    },
    showDeleteAction(agent) {
      if (this.currentUserId === agent.id) {
        return false;
      }

      if (!agent.confirmed) {
        return true;
      }

      if (this.agentRoles.includes(agent.role)) {
        return this.verifiedAdministrators().length !== 1;
      }
      return true;
    },
    verifiedAdministrators() {
      return this.agentList.filter(
        agent => this.agentRoles.includes(agent.role) && agent.confirmed
      );
    },
    // Edit Function
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup(fetch = false) {      
      this.showAddPopup = false;
      if (fetch === true) {
        this.fetch(this.pageParameter, false);
      }
    },

    // Edit Function
    openEditPopup(agent) {
      this.showEditPopup = true;
      this.currentAgent = agent;
    },
    hideEditPopup(fetch = false) {
      this.showEditPopup = false;
      if (fetch === true) {
        this.fetch(this.pageParameter, false);
      }
    },
    openCustomAttributePopup(agent) {
      this.showCustomAttributePopup = true;
      this.currentAgent = agent;
    },
    hideCustomAttributePopup() {
      this.showCustomAttributePopup = false;
    },

    // Delete Function
    openDeletePopup(agent) {
      this.showDeletePopup = true;
      this.currentAgent = agent;
    },
    closeDeletePopup() {
      this.showDeletePopup = false;
    },
    confirmDeletion() {
      this.loading[this.currentAgent.id] = true;
      this.closeDeletePopup();
      this.deleteAgent(this.currentAgent.id);
    },
    async deleteAgent(id) {
      try {
        await this.$store.dispatch('agents/delete', {agentId: id, accountAndSubaccounts: this.isAccountTypeParent});
        this.fetch(this.pageParameter, false);
        this.showAlert(this.$t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
      }
    },
    updatePageParam(page) {
      let url = `${this.$route.path}?page=${page}`;
      if(this.search.q.trim() != ''){
        url += `&q=${this.search.q}`;
      }
      if(this.search.account_id != null){
        url += `&account_id=${this.search.account_id}`;
      }
      window.history.pushState({}, null, url);
    },
    async fetch(page, pushState = true) {
      if(pushState){
        this.updatePageParam(page);
      }
      let hasSearch = this.search.q.trim() != '' || this.search.account_id != null;

      if (!hasSearch) {
        this.$store.dispatch('agents/index', {page, includeSubaccounts: this.isAccountTypeParent});
      } else {        
        this.$store.dispatch('agents/search', {
          ...this.search,
          page,
          search: this.search.q,
          includeSubaccounts: this.isAccountTypeParent,
        });
      }
    },
    // Show SnackBar
    showAlert(message) {
      // Reset loading, current selected agent
      this.loading[this.currentAgent.id] = false;
      this.currentAgent = {};
      // Show message
      this.agentAPI.message = message;
      bus.$emit('newToastMessage', message);
    },
    onPageChange(page) {      
      this.fetch(page);
    },   
    onSearchSubmit(dataSearch) {      
      this.search = dataSearch;
      this.fetch(DEFAULT_PAGE);
    },
    getDataSearchFromQueryParams(){
      let data = {q: '', account_id: null};
      if(this.$route.query?.q && this.$route.query?.q.trim() != ''){
        data.q = this.$route.query.q;
      }
      if(this.$route.query?.account_id && this.$route.query?.account_id.trim() != ''){
        data.account_id = this.$route.query.account_id;
      }
      return Object.keys(data).length > 0 ? data : null;
      
    }
  },
};
</script>

<style scoped lang="scss">
.woot-table {
  margin-bottom: var(--space-larger);
  .column-actions {
    width: 90px;
  }
}
</style>
